import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Select,
  Space,
  Typography,
  Alert,
  Spin,
  message,
  Descriptions,
  Tag,
  Divider,
  Row,
  Col,
  DatePicker,
  Modal,
  Form,
  Input,
  Switch,
} from "antd";
import {
  SyncOutlined,
  DatabaseOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EditOutlined,
  UserOutlined,
  BuildOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import apiClient from "../../utils/axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { Option } = Select;

interface Organization {
  id: string;
  name: string;
  subscription_status: string;
  billing_cycle: string;
  stripe_customer_id?: string;
  free_trial_expires_at?: string;
  owner_email?: string;
  created_at?: string;
  billing_mode?: string;
  payment_status?: string;
  facility_count?: number;
  active_facility_count?: number;
  user_count?: number;
}

interface SyncResult {
  success: boolean;
  message: string;
  sync_result?: {
    sync_performed?: boolean;
    old_status?: string;
    new_status?: string;
    stripe_status?: string;
    previous_db_trial_end?: string;
    new_db_trial_end?: string;
    billing_cycle_synced?: string;
  };
  error?: string;
}

interface TrialUpdateData {
  free_trial_expires_at?: string;
  extend_days?: number;
  remove_trial?: boolean;
}

export const OrganizationAdmin: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [trialModalVisible, setTrialModalVisible] = useState(false);
  const [trialForm] = Form.useForm();
  const [updatingTrial, setUpdatingTrial] = useState(false);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Fetch all organizations (admin endpoint)
      const response = await apiClient.get("/api/admin/organizations", {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setOrganizations(response.data.organizations || []);
    } catch (error: any) {
      console.error("Error fetching organizations:", error);
      message.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleStripeSync = async () => {
    if (!selectedOrgId) {
      message.warning("Please select an organization to sync");
      return;
    }

    setSyncing(true);
    setSyncResult(null);

    try {
      const token = await getAccessTokenSilently();
      
      // Call the sync endpoint
      const response = await apiClient.post(
        `/api/organization/${selectedOrgId}/stripe/sync-subscription-status`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000, // 30 second timeout for admin operations
        }
      );

      setSyncResult(response.data);
      
      if (response.data.success) {
        message.success("Stripe sync completed successfully!");
        // Refresh organizations to show updated data
        await fetchOrganizations();
      } else {
        message.error(`Sync failed: ${response.data.message}`);
      }
    } catch (error: any) {
      console.error("Error syncing with Stripe:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Sync failed";
      setSyncResult({
        success: false,
        message: errorMessage,
        error: errorMessage
      });
      message.error(`Sync failed: ${errorMessage}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleTrialUpdate = async (values: any) => {
    if (!selectedOrgId) return;

    setUpdatingTrial(true);
    try {
      const token = await getAccessTokenSilently();
      
      const updateData: TrialUpdateData = {};
      
      if (values.action === 'set_date' && values.trial_date) {
        updateData.free_trial_expires_at = values.trial_date.toISOString();
      } else if (values.action === 'extend' && values.extend_days) {
        updateData.extend_days = parseInt(values.extend_days);
      } else if (values.action === 'remove') {
        updateData.remove_trial = true;
      }

      const response = await apiClient.post(
        `/api/admin/organizations/${selectedOrgId}/trial`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000,
        }
      );

      if (response.data.success) {
        message.success(response.data.message || "Trial updated successfully!");
        setTrialModalVisible(false);
        trialForm.resetFields();
        await fetchOrganizations();
      } else {
        message.error(response.data.message || "Failed to update trial");
      }
    } catch (error: any) {
      console.error("Error updating trial:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to update trial";
      message.error(errorMessage);
    } finally {
      setUpdatingTrial(false);
    }
  };

  const selectedOrg = organizations.find(org => org.id === selectedOrgId);

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text>Loading organizations...</Text>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <SettingOutlined /> Organization Admin Tool
      </Title>
      
      <Alert
        message="Admin Tool"
        description="Comprehensive organization management tool for administrators. Manage subscriptions, trials, and sync data with Stripe. Only organizations with existing Stripe customers are shown for sync operations."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title="Organization Selection" style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong>Select Organization:</Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Choose an organization to manage"
                  value={selectedOrgId}
                  onChange={setSelectedOrgId}
                  showSearch
                  dropdownStyle={{ maxHeight: 400 }}
                  filterOption={(input, option) => {
                    const searchText = input.toLowerCase();
                    const org = organizations.find(o => o.id === option?.value);
                    if (!org) return false;
                    
                    const orgName = org.name.toLowerCase();
                    const ownerEmail = (org.owner_email || '').toLowerCase();
                    
                    return orgName.includes(searchText) || ownerEmail.includes(searchText);
                  }}
                >
                  {organizations.map(org => (
                    <Option key={org.id} value={org.id}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                          <Text strong style={{ whiteSpace: 'nowrap' }}>{org.name}</Text>
                          {org.owner_email && (
                            <Text type="secondary" style={{ fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              ({org.owner_email})
                            </Text>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          <Tag color={org.subscription_status === 'Active' ? 'green' : 'orange'} style={{ margin: 0 }}>
                            {org.subscription_status}
                          </Tag>
                          <Tag color="blue" style={{ margin: 0 }}>
                            <CreditCardOutlined /> Stripe
                          </Tag>
                        </div>
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>

              <div>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    icon={<SyncOutlined />}
                    onClick={handleStripeSync}
                    loading={syncing}
                    disabled={!selectedOrgId}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    {syncing ? 'Syncing with Stripe...' : 'Sync from Stripe'}
                  </Button>
                  
                  <Button
                    icon={<CalendarOutlined />}
                    onClick={() => setTrialModalVisible(true)}
                    disabled={!selectedOrgId}
                    size="large"
                    style={{ width: '100%' }}
                  >
                    Manage Free Trial
                  </Button>
                </Space>
              </div>
            </Space>
          </Card>

          {selectedOrg && (
            <Card title="Organization Details" style={{ marginBottom: 24 }}>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Name">
                  {selectedOrg.name}
                </Descriptions.Item>
                <Descriptions.Item label="Owner Email">
                  {selectedOrg.owner_email ? (
                    <Text code>{selectedOrg.owner_email}</Text>
                  ) : (
                    <Text type="secondary">Not available</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {selectedOrg.created_at ? (
                    <Text>{new Date(selectedOrg.created_at).toLocaleDateString()}</Text>
                  ) : (
                    <Text type="secondary">Not available</Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Tag color={selectedOrg.subscription_status === 'Active' ? 'green' : 'orange'}>
                    {selectedOrg.subscription_status}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Billing Cycle">
                  {selectedOrg.billing_cycle}
                </Descriptions.Item>
                <Descriptions.Item label="Billing Mode">
                  {selectedOrg.billing_mode ? (
                    <Tag color={selectedOrg.billing_mode === 'CreditCard' ? 'blue' : 'purple'}>
                      {selectedOrg.billing_mode}
                    </Tag>
                  ) : (
                    <Text type="secondary">Not set</Text>
                  )}
                </Descriptions.Item>
                {selectedOrg.payment_status && (
                  <Descriptions.Item label="Payment Status">
                    <Tag color={selectedOrg.payment_status === 'Current' ? 'green' : 'red'}>
                      {selectedOrg.payment_status}
                    </Tag>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Stripe Customer">
                  <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Text code>{selectedOrg.stripe_customer_id}</Text>
                  </Space>
                </Descriptions.Item>
                {selectedOrg.free_trial_expires_at && (
                  <Descriptions.Item label="Trial Expires">
                    <Space>
                      <CalendarOutlined />
                      <Text>
                        {new Date(selectedOrg.free_trial_expires_at).toLocaleDateString()}
                      </Text>
                      <Text type="secondary">
                        ({dayjs(selectedOrg.free_trial_expires_at).fromNow()})
                      </Text>
                    </Space>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Facilities">
                  <Space>
                    <BuildOutlined />
                    <Text>
                      {selectedOrg.facility_count || 0} total
                      {selectedOrg.active_facility_count !== undefined && (
                        <Text type="secondary"> ({selectedOrg.active_facility_count} active)</Text>
                      )}
                    </Text>
                  </Space>
                </Descriptions.Item>
                {selectedOrg.user_count !== undefined && (
                  <Descriptions.Item label="Users">
                    <Space>
                      <UserOutlined />
                      <Text>{selectedOrg.user_count}</Text>
                    </Space>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          )}
        </Col>

        <Col xs={24} lg={12}>
          {syncResult && (
            <Card 
              title={
                <Space>
                  <DatabaseOutlined />
                  Sync Results
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Alert
                message={syncResult.success ? "Sync Successful" : "Sync Failed"}
                description={syncResult.message}
                type={syncResult.success ? "success" : "error"}
                showIcon
                style={{ marginBottom: 16 }}
              />

              {syncResult.sync_result && (
                <>
                  <Divider />
                  <Descriptions column={1} size="small">
                    {syncResult.sync_result.sync_performed !== undefined && (
                      <Descriptions.Item label="Changes Made">
                        <Tag color={syncResult.sync_result.sync_performed ? 'green' : 'blue'}>
                          {syncResult.sync_result.sync_performed ? 'Yes' : 'No changes needed'}
                        </Tag>
                      </Descriptions.Item>
                    )}
                    
                    {syncResult.sync_result.old_status && syncResult.sync_result.new_status && (
                      <Descriptions.Item label="Status Update">
                        <Space>
                          <Tag color="orange">{syncResult.sync_result.old_status}</Tag>
                          <Text>→</Text>
                          <Tag color="green">{syncResult.sync_result.new_status}</Tag>
                        </Space>
                      </Descriptions.Item>
                    )}
                    
                    {syncResult.sync_result.stripe_status && (
                      <Descriptions.Item label="Stripe Status">
                        <Tag color="blue">{syncResult.sync_result.stripe_status}</Tag>
                      </Descriptions.Item>
                    )}
                    
                    {syncResult.sync_result.previous_db_trial_end && syncResult.sync_result.new_db_trial_end && (
                      <Descriptions.Item label="Trial Date Update">
                        <Space direction="vertical" size="small">
                          <div>
                            <Text type="secondary">Before: </Text>
                            <Text>{new Date(syncResult.sync_result.previous_db_trial_end).toLocaleDateString()}</Text>
                          </div>
                          <div>
                            <Text type="secondary">After: </Text>
                            <Text>{new Date(syncResult.sync_result.new_db_trial_end).toLocaleDateString()}</Text>
                          </div>
                        </Space>
                      </Descriptions.Item>
                    )}
                    
                    {syncResult.sync_result.billing_cycle_synced && (
                      <Descriptions.Item label="Billing Cycle">
                        <Tag color="blue">{syncResult.sync_result.billing_cycle_synced}</Tag>
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </>
              )}

              {syncResult.error && (
                <>
                  <Divider />
                  <Alert
                    message="Error Details"
                    description={syncResult.error}
                    type="error"
                    showIcon
                  />
                </>
              )}
            </Card>
          )}

          <Card title="How to Use" size="small">
            <Space direction="vertical" size="small">
              <div>
                <Text strong>1. Select Organization</Text>
                <br />
                <Text type="secondary">Choose the organization you want to manage from the dropdown.</Text>
              </div>
              <div>
                <Text strong>2. Review Details</Text>
                <br />
                <Text type="secondary">Check the organization status, billing info, and current trial dates.</Text>
              </div>
              <div>
                <Text strong>3. Sync with Stripe</Text>
                <br />
                <Text type="secondary">Update subscription status and trial dates from Stripe data.</Text>
              </div>
              <div>
                <Text strong>4. Manage Trial</Text>
                <br />
                <Text type="secondary">Extend, modify, or remove free trial periods. Changes sync to Stripe automatically.</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Trial Management Modal */}
      <Modal
        title="Manage Free Trial"
        open={trialModalVisible}
        onCancel={() => {
          setTrialModalVisible(false);
          trialForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={trialForm}
          layout="vertical"
          onFinish={handleTrialUpdate}
        >
          <Alert
            message="Trial Management"
            description={`Managing trial for: ${selectedOrg?.name}. Changes will be applied to both the database and Stripe (if subscription exists).`}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            name="action"
            label="Action"
            rules={[{ required: true, message: 'Please select an action' }]}
          >
            <Select placeholder="Choose what to do with the trial">
              <Option value="set_date">Set specific end date</Option>
              <Option value="extend">Extend by days</Option>
              <Option value="remove">Remove trial</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.action !== currentValues.action
            }
          >
            {({ getFieldValue }) => {
              const action = getFieldValue('action');
              
              if (action === 'set_date') {
                return (
                  <Form.Item
                    name="trial_date"
                    label="Trial End Date"
                    rules={[{ required: true, message: 'Please select a date' }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }}
                      showTime={false}
                      placeholder="Select trial end date"
                      disabledDate={(current) => current && current < dayjs().startOf('day')}
                    />
                  </Form.Item>
                );
              }
              
              if (action === 'extend') {
                return (
                  <Form.Item
                    name="extend_days"
                    label="Extend by Days"
                    rules={[
                      { required: true, message: 'Please enter number of days' },
                      { pattern: /^\d+$/, message: 'Please enter a valid number' }
                    ]}
                  >
                    <Input 
                      placeholder="Number of days to extend (e.g., 7)"
                      type="number"
                      min={1}
                      max={365}
                    />
                  </Form.Item>
                );
              }
              
              if (action === 'remove') {
                return (
                  <Alert
                    message="Remove Trial"
                    description="This will remove the free trial period entirely. The organization will need to have an active subscription to continue using the service."
                    type="warning"
                    showIcon
                  />
                );
              }
              
              return null;
            }}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={updatingTrial}
                icon={<CalendarOutlined />}
              >
                Update Trial
              </Button>
              <Button onClick={() => {
                setTrialModalVisible(false);
                trialForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}; 